import type { components } from '@tabservice/tabfile-client';
import type { EFileRecord } from '@tabservice/tabfile-client';
import type { Tone } from '@/components/ui/pill';

type Schemas = components['schemas'];

/** One record with its data, its IRS result and its fix state. */
export type EFileRecordDetail = Schemas['EFileRecordDetail'];
export type RecordFix = Schemas['RecordFix'];
export type FixState = RecordFix['state'];
/** Whether a queued fix will be filed as a correction or as a replacement. */
export type FixQueuedFor = NonNullable<RecordFix['queuedFor']>;

export interface FixMeta {
  label: string;
  tone: Tone;
}

/**
 * The pill each fix state carries. `none` has none: a record nobody has touched
 * is the ordinary case, and a pill on every row would say nothing.
 */
export const fixStateMeta: Record<FixState, FixMeta | null> = {
  none: null,
  queued: { label: 'Fix queued', tone: 'info' },
  submitted: { label: 'Fix sent', tone: 'info' },
  corrected: { label: 'Corrected', tone: 'success' },
  fix_failed: { label: 'Fix refused', tone: 'danger' },
};

export function fixStateOf(record: Pick<EFileRecord, 'fix'>): FixState {
  return record.fix?.state ?? 'none';
}

/**
 * The pill for one record. `submitted` and `corrected` say which kind of e-file carried the
 * fix, because a replacement and a correction are different things to the IRS.
 */
export function fixMetaFor(record: Pick<EFileRecord, 'fix'>): FixMeta | null {
  const state = fixStateOf(record);
  const kind = record.fix?.queuedFor === 'replacement' ? 'Replacement' : 'Correction';
  if (state === 'submitted') return { label: `${kind} sent`, tone: 'info' };
  if (state === 'corrected') return { label: kind === 'Replacement' ? 'Replaced' : 'Corrected', tone: 'success' };
  if (state === 'fix_failed') return { label: `${kind} refused`, tone: 'danger' };
  return fixStateMeta[state];
}

/**
 * A record the IRS refused or accepted with an error, that nobody has decided
 * anything about yet. It is the only group with work outstanding.
 */
export function needsAttention(record: Pick<EFileRecord, 'status' | 'fix'>): boolean {
  // A fix the IRS refused needs a person again, wherever the record itself stands.
  if (fixStateOf(record) === 'fix_failed') return true;
  if (record.status !== 'error' && record.status !== 'rejected') return false;
  return fixStateOf(record) === 'none';
}

export type FixFilter = 'all' | 'attention' | 'queued' | 'submitted' | 'corrected' | 'fix_failed';

export const fixFilterOptions: { value: FixFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'attention', label: 'Needs attention' },
  { value: 'queued', label: 'Fix queued' },
  { value: 'submitted', label: 'Fix sent' },
  { value: 'corrected', label: 'Corrected' },
  { value: 'fix_failed', label: 'Fix refused' },
];

export function matchesFixFilter(record: Pick<EFileRecord, 'status' | 'fix'>, filter: FixFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'attention') return needsAttention(record);
  return fixStateOf(record) === filter;
}

/**
 * The sentence above the table. It is the two numbers worth acting on, and it
 * says nothing at all when there is nothing outstanding and nothing queued.
 */
export function fixSummaryLine(attention: number, queued: number): string | null {
  if (attention === 0 && queued === 0) return null;
  const parts: string[] = [];
  if (attention > 0) parts.push(`${attention} ${attention === 1 ? 'record needs' : 'records need'} attention`);
  if (queued > 0) parts.push(`${queued} ${queued === 1 ? 'fix' : 'fixes'} queued`);
  return `${parts.join(', ')}.`;
}

/** What a queued fix will be filed as, when the record itself does not say. */
export function queuedForOf(records: Pick<EFileRecord, 'fix'>[], fallback: FixQueuedFor): FixQueuedFor {
  for (const record of records) {
    const queuedFor = record.fix?.queuedFor;
    if (queuedFor) return queuedFor;
  }
  return fallback;
}
